import * as React from 'react';
import { config, easings, type SpringValues } from '@react-spring/web';
import { animated, useTransition } from '@react-spring/web';
import { CrossIcon } from '@mentimeter/ragnar-visuals';
import { UpvoteIndicator } from '@mentimeter/slides-presentation-ui/groups/upvote-indicator';
import { useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { useTranslations } from 'next-intl';
import { ModerationButton } from '../ui/ModerationButton';
import type { OpenEndedResponse } from '../types';
import { Hotkey } from '../../../../shared/components/Hotkey';
import { NavigationButton } from './NavigationButton';
import { FocusedResponse } from './FocusedResponse';

const INACTIVE_ITEM_SCALE = 0.75;

export interface FocusedViewProps {
  onDismiss: () => void;
  responses: OpenEndedResponse[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  showUpvotes: boolean;
  readonly?: boolean;
}

export const FocusedView = ({
  onDismiss,
  responses,
  currentIndex,
  setCurrentIndex,
  showUpvotes,
  readonly,
}: FocusedViewProps) => {
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down'>(
    'down',
  );

  const translate = useTranslations('presenting.legacy');

  const handleNavigateToPrevious = () => {
    setAnimationDirection('up');
    setCurrentIndex(currentIndex - 1);
  };

  const handleNavigateToNext = () => {
    setAnimationDirection('down');
    setCurrentIndex(currentIndex + 1);
  };

  const transition = useTransition(currentIndex, {
    from: {
      opacity: 0,
      transform: `translate3d(0, ${
        animationDirection === 'up' ? '50px' : '-50px'
      }, 0) scale(${1})`,
    },
    enter: {
      opacity: 1,
      transform: 'translate3d(0, 0%, 0) scale(1)',
      config: {
        duration: 200,
        easing: easings.easeOutQuad,
      },
    },
    leave: {
      opacity: 0,
      transform: `translate3d(0, ${
        animationDirection === 'down' ? '40%' : '-40%'
      }, 0) scale(${INACTIVE_ITEM_SCALE})`,
      config: {
        duration: 200,
      },
    },
    exitBeforeEnter: true,
    config: config.molasses,
  });

  return (
    <Box
      flex="1 1 auto"
      justifyContent="space-between"
      alignItems="center"
      height="100%"
      width="100%"
      py="space20"
    >
      <NavigationButton
        direction="up"
        disabled={readonly || currentIndex <= 0}
        onClick={handleNavigateToPrevious}
        aria-label={translate('actions.show_previous')}
        zIndex={2}
      />
      <Box
        flex={1}
        width="100%"
        alignItems="center"
        justifyContent="center"
        style={{ isolation: 'isolate' }}
      >
        {transition((styles, currentIndex) => {
          const currentResponse = responses[currentIndex];
          const currentResponseText = currentResponse?.responseText ?? '';
          const voteCount = responses[currentIndex]?.voteCount ?? 0;
          const maxVoteCount = Math.max(...responses.map((v) => v.voteCount));
          const shouldShowUpvotes = showUpvotes && maxVoteCount > 0;

          return (
            <FocusedResponse
              style={styles as SpringValues}
              as={animated.div}
              minHeight={shouldShowUpvotes ? '480px' : 'unset'}
              subheading={translate('message.current_response_index_label', {
                currentIndex: `${currentIndex + 1}`,
                total: `${responses.length}`,
              })}
              responseText={currentResponseText}
              participantDisplayName={
                currentResponse?.participantDisplayName ?? null
              }
              UpvotesComponent={
                shouldShowUpvotes ? (
                  <UpvoteIndicator
                    upvotes={voteCount}
                    isTopVoted={voteCount === maxVoteCount}
                  />
                ) : null
              }
              Controls={
                readonly ? null : (
                  <>
                    <ModerationButton
                      selectedAnswer={currentResponseText}
                      color="onNeutralWeak"
                    />
                    <IconButton
                      onClick={onDismiss}
                      size="large"
                      variant="secondary"
                      borderRadius="medium"
                      bg="transparent"
                      color="onNeutralWeak"
                      aria-label={translate('actions.go_back')}
                    >
                      <Hotkey keyCode="escape" action={onDismiss} />
                      <CrossIcon size={3} />
                    </IconButton>
                  </>
                )
              }
            />
          );
        })}
      </Box>
      <NavigationButton
        direction="down"
        disabled={readonly || currentIndex >= responses.length - 1}
        aria-label={translate('actions.show_next')}
        onClick={handleNavigateToNext}
      />
    </Box>
  );
};
